import PropTypes from 'prop-types';
import React, { useContext, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import Isvg from 'react-inlinesvg';
import { useDispatch, useSelector } from 'react-redux';

import { DropdownPillMenu } from 'components/Dropdown';
import { Flyout, FlyoutContent, FlyoutFooter } from 'components/Flyout';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import { Button, ButtonGroup, Form } from 'components/HookForm';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { createSuccessSnackbar, createErrorSnackbar } from 'components/Snackbar';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { selectActiveVenueId } from 'selectors/general';
import ClassLevelMinimumSpotsContext from '../Contexts/ClassLevelMinimumSpotsContext';
import { messages } from './ClassLevelMinimumSpotsFlyout.messages';
import ClassTableContent from './ClassTableContent';
import PeakTimeDefinition from './PeakTimeDefinition';
import { trackClassLevelMinimumSpotsUpdated } from './SmartTools.helpers';

import './ClassLevelMinimumSpotsFlyout.scss';

const { block, element } = bem('class-level-minimum-spots-flyout');
const FORM_NAME = 'classLevelMinimumSpotsFlyout';

const renderSpotsDropdown = (control, name, options) => (
  <Controller
    control={control}
    name={name}
    render={({ field: { value, onChange } }) => (
      <DropdownPillMenu
        attachment="left"
        items={options}
        getKey="key"
        getText="label"
        isSelected={({ key }) => key === value}
        onSelect={({ key }) => onChange(key)}
        renderSelectedItem={text => text ?? options[0].label}
        renderAsActive={false}
      />
    )}
  />
);

const NO_SCROLL_CLASS = 'no-scroll';

const ClassLevelMinimumSpotsFlyout = ({ flyoutId, countryCode = 'USA' }) => {
  const dispatch = useDispatch();
  const venueId = useSelector(selectActiveVenueId);
  const { closeFlyout, openFlyoutId } = useContext(FlyoutContext);
  const isFlyoutOpen = openFlyoutId === flyoutId;

  useEffect(() => {
    if (isFlyoutOpen) {
      document.documentElement.classList.add(NO_SCROLL_CLASS);
      document.body.classList.add(NO_SCROLL_CLASS);
    } else {
      document.documentElement.classList.remove(NO_SCROLL_CLASS);
      document.body.classList.remove(NO_SCROLL_CLASS);
    }
    return () => {
      document.documentElement.classList.remove(NO_SCROLL_CLASS);
      document.body.classList.remove(NO_SCROLL_CLASS);
    };
  }, [isFlyoutOpen]);

  const {
    minimumSpotsData: minimumSpotsDataFromQuery,
    venueClasses,
    venueLevelDefaults,
    isLoading,
    error,
    fetchData,
    updateMinimumSpots,
  } = useContext(ClassLevelMinimumSpotsContext);

  function overrideFlyoutOutsideClick() {
    return false;
  }

  const buildDefaultValues = () => {
    if (!venueClasses || venueClasses.length === 0) {
      return { classes: {} };
    }

    const classes = venueClasses.reduce((acc, classItem) => {
      const { classId } = classItem;
      const classData = minimumSpotsDataFromQuery[classId] ?? {
        minimumSpotsPeakTime: null,
        minimumSpotsOffpeakTime: null,
      };
      acc[classId] = {
        minimumSpotsPeakTime: classData.minimumSpotsPeakTime,
        minimumSpotsOffpeakTime: classData.minimumSpotsOffpeakTime,
      };
      return acc;
    }, {});

    return { classes };
  };

  const dirtyValues = (dirtyFields, formValues) => {
    if (!dirtyFields || !formValues) {
      return {};
    }

    return Object.keys(dirtyFields).reduce((acc, classId) => {
      const classDirtyFields = dirtyFields[classId];
      const classFormValues = formValues[classId];

      if (classDirtyFields && classFormValues) {
        acc[classId] = Object.keys(classDirtyFields).reduce((fieldAcc, fieldName) => {
          if (classDirtyFields[fieldName]) {
            fieldAcc[fieldName] = classFormValues[fieldName];
          }
          return fieldAcc;
        }, {});
      }

      return acc;
    }, {});
  };

  const handleFormSubmit = async (formValues, dirtyFields) => {
    try {
      const dirtyValuesResult = dirtyValues(dirtyFields.classes, formValues.classes);
      await updateMinimumSpots(dirtyValuesResult);
      trackClassLevelMinimumSpotsUpdated(venueId, dirtyValuesResult);
      dispatch(createSuccessSnackbar(messages.successSave));
      closeFlyout();
    } catch (saveError) {
      dispatch(createErrorSnackbar(messages.errorSave));
    }
  };

  const renderBody = control => {
    if (isLoading) {
      return (
        <div className={element('loading')}>
          <LoadingDots />
        </div>
      );
    }
    if (error) {
      return (
        <div className={element('error')}>
          <Message message={messages.errorClassData} />
          <Message
            className={element('error-btn')}
            as={Button}
            message={messages.errorTryAgain}
            onClick={fetchData}
            styled="primary"
            type={Button.TYPES.BUTTON}
          />
        </div>
      );
    }
    if (!venueClasses || venueClasses.length === 0) {
      return (
        <div className={element('empty')}>
          <Message message={messages.noClassesFound} />
        </div>
      );
    }
    return (
      <ClassTableContent control={control} venueClasses={venueClasses} renderSpotsDropdown={renderSpotsDropdown} />
    );
  };

  return (
    <Flyout id={flyoutId} className={block()} overrideOutsideClick={overrideFlyoutOutsideClick}>
      <Form id={FORM_NAME} defaultValues={buildDefaultValues()}>
        {function FormRender({ control, formState, reset, handleSubmit }) {
          useEffect(() => {
            reset(buildDefaultValues());
          }, [venueClasses, minimumSpotsDataFromQuery, venueLevelDefaults, reset]);

          const { dirtyFields, isDirty, isSubmitting } = formState;

          return (
            <>
              <FlyoutContent>
                <div className={element('content-wrapper')}>
                  <div className={element('content-header')}>
                    <div className={element('title')}>
                      <Message message={messages.title} />
                    </div>
                    <div className={element('description')}>
                      <Message message={messages.description} />
                    </div>
                  </div>
                  <div className={element('definitions')}>
                    <PeakTimeDefinition countryCode={countryCode} />
                  </div>
                  <div className={element('venue-level-info')}>
                    <span className={element('venue-level-info-icon')}>
                      <Isvg src={ICONS.infoGreyLight} />
                    </span>
                    <Message message={messages.venueLevelInfo} />
                  </div>
                  {renderBody(control)}
                </div>
              </FlyoutContent>
              <FlyoutFooter>
                <ButtonGroup className={element('button-group')} divider={false} flush>
                  <Message
                    as={Button}
                    className={element('cancel-btn')}
                    message={messages.cancel}
                    onClick={closeFlyout}
                    styled="tertiary"
                    type={Button.TYPES.BUTTON}
                  />
                  <Message
                    as={Button}
                    className={element('save-btn')}
                    message={messages.save}
                    onClick={handleSubmit(formValues => handleFormSubmit(formValues, dirtyFields))}
                    styled="primary"
                    type={Button.TYPES.BUTTON}
                    disabled={!isDirty || isSubmitting}
                  />
                </ButtonGroup>
              </FlyoutFooter>
            </>
          );
        }}
      </Form>
    </Flyout>
  );
};

ClassLevelMinimumSpotsFlyout.propTypes = {
  flyoutId: PropTypes.string.isRequired,
  countryCode: PropTypes.string,
};

export default ClassLevelMinimumSpotsFlyout;
